export const PDS_PRINT_STYLES = `
  html, body {
    margin: 0;
    padding: 0;
    background: #fff;
    font-family: Arial, Helvetica, sans-serif;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }
  .pds-print-root {
    width: 100%;
    max-width: 210mm;
    margin: 0 auto;
  }
  .pds-page {
    width: 100%;
    box-sizing: border-box;
    page-break-after: always;
    break-after: page;
  }
  .pds-page:last-child {
    page-break-after: auto;
    break-after: auto;
  }
  table { page-break-inside: auto; }
  tr { page-break-inside: avoid; }
  input[type="checkbox"] {
    width: 9px;
    height: 9px;
    margin: 0 2px 0 0;
    vertical-align: middle;
    accent-color: #000;
  }
  @page {
    size: A4 portrait;
    margin: 8mm 6mm 10mm 6mm;
  }
  @media print {
    html, body { width: 210mm; }
    .pds-print-root { max-width: none; }
  }
`;

const EDUCATION_LEVELS = [
  { label: "ELEMENTARY", keys: ["ELEMENTARY"] },
  { label: "SECONDARY", keys: ["SECONDARY"] },
  {
    label: "VOCATIONAL / TRADE COURSE",
    keys: ["VOCATIONAL / TRADE COURSE", "VOCATIONAL/TRADE COURSE"],
  },
  { label: "COLLEGE", keys: ["COLLEGE"] },
  { label: "GRADUATE STUDIES", keys: ["GRADUATE STUDIES"] },
];

function formatPdsDate(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${date.getFullYear()}`;
}

function formatPdsYear(dateStr) {
  if (!dateStr) return "";
  const raw = String(dateStr).trim();
  if (/^\d{4}$/.test(raw)) return raw;
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "";
  return String(date.getFullYear());
}

function isYes(value) {
  if (value === true) return true;
  if (value === false || value == null) return false;
  if (typeof value === "string") {
    const v = value.trim().toUpperCase();
    if (!v || v === "NO" || v === "N" || v === "FALSE") return false;
    return true;
  }
  return Boolean(value);
}

function matchesCivil(value, labels) {
  const current = String(value || "")
    .trim()
    .toUpperCase();
  return labels.some((label) => current === label.toUpperCase());
}

function matchesCitizenship(value, labels) {
  const current = String(value || "").trim();
  return labels.some((label) => current.toUpperCase() === label.toUpperCase());
}

function findEducationRow(rows, keys) {
  return (rows || []).find((row) =>
    keys.some(
      (key) =>
        String(row?.educationLevel || "").toUpperCase() === key.toUpperCase(),
    ),
  );
}

export default function PrintPDSTable({ object }) {
  const employee = object?.employee || {};

  const border = "1px solid #000";
  const labelBg = { backgroundColor: "#d9d9d9" };
  const headerBg = {
    backgroundColor: "#808080",
    color: "#fff",
    fontWeight: "bold",
    fontStyle: "italic",
    textAlign: "left",
    padding: "2px 4px",
    border,
  };
  const cell = {
    border,
    padding: "2px 3px",
    verticalAlign: "top",
  };
  const labelCell = {
    ...cell,
    ...labelBg,
  };
  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "fixed",
    fontSize: "7.5px",
    lineHeight: 1.25,
    border,
  };
  const centerLabel = {
    ...labelCell,
    textAlign: "center",
    fontWeight: "bold",
  };
  const warningRow = {
    ...cell,
    ...labelBg,
    textAlign: "center",
    color: "#c00",
    fontStyle: "italic",
    fontWeight: "bold",
  };

  const Check = ({ checked }) => (
    <input
      type="checkbox"
      checked={!!checked}
      readOnly
      onChange={() => {}}
    />
  );

  const sectionHeader = (title, colSpan = 8) => (
    <tr>
      <th colSpan={colSpan} style={headerBg}>
        {title}
      </th>
    </tr>
  );

  const signatureRow = (totalCols) => {
    if (totalCols <= 3) {
      return (
        <tr>
          <td style={centerLabel}>SIGNATURE</td>
          <td style={{ ...cell, textAlign: "center" }}>
            (e-signature/digital certificate)
          </td>
          <td style={cell}>
            <b>DATE</b> {formatPdsDate(new Date())}
          </td>
        </tr>
      );
    }
    const remaining = totalCols - 2;
    const sigValue = Math.ceil(remaining / 2);
    const dateValue = remaining - sigValue;
    return (
      <tr>
        <td style={centerLabel}>SIGNATURE</td>
        <td colSpan={sigValue} style={{ ...cell, textAlign: "center" }}>
          (e-signature/digital certificate)
        </td>
        <td style={centerLabel}>DATE</td>
        <td colSpan={dateValue} style={cell}>
          {formatPdsDate(new Date())}
        </td>
      </tr>
    );
  };

  const continueNote = (text, colSpan) => (
    <tr>
      <td colSpan={colSpan} style={warningRow}>
        {text}
      </td>
    </tr>
  );

  const HeaderPage = () => (
    <div style={{ padding: "2px 3px 4px", fontFamily: "Arial, sans-serif" }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div style={{ fontSize: "8px" }}>
          <strong>CS Form No. 212</strong>
          <br />
          <i>Revised 2026</i>
        </div>
        <div
          style={{
            flex: 1,
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "16px",
            letterSpacing: "0.5px",
          }}
        >
          PERSONAL DATA SHEET
        </div>
        <div style={{ width: "70px" }} />
      </div>
      <div style={{ marginTop: "3px", fontSize: "7px", fontWeight: "bold" }}>
        WARNING: Any misrepresentation made in the Personal Data Sheet and the
        Work Experience Sheet shall cause the filing of administrative/criminal
        case/s against the person concerned.
      </div>
      <div style={{ fontSize: "7px", fontWeight: "bold", marginTop: "2px" }}>
        READ THE ATTACHED GUIDE TO FILLING OUT THE PERSONAL DATA SHEET (PDS)
        BEFORE ACCOMPLISHING THE PDS FORM.
      </div>
      <div style={{ fontSize: "7px", marginTop: "1px" }}>
        Tick appropriate boxes (
        <input type="checkbox" readOnly />) and use separate sheet if necessary.
        Indicate N/A if not applicable. <b>DO NOT ABBREVIATE.</b>
      </div>
    </div>
  );

  const addressPair = (leftLabel, leftValue, rightLabel, rightValue) => (
    <div>
      <div style={{ display: "flex", textAlign: "center" }}>
        <div style={{ width: "50%" }}>
          <div>{leftValue || "\u00A0"}</div>
          <div
            style={{
              fontSize: "6px",
              fontStyle: "italic",
              borderTop: "1px solid #666",
            }}
          >
            {leftLabel}
          </div>
        </div>
        <div style={{ width: "50%" }}>
          <div>{rightValue || "\u00A0"}</div>
          <div
            style={{
              fontSize: "6px",
              fontStyle: "italic",
              borderTop: "1px solid #666",
            }}
          >
            {rightLabel}
          </div>
        </div>
      </div>
    </div>
  );

  const PersonalInformationTable = () => {
    const civil = employee?.civil;
    const citizenship = employee?.citizenship;
    return (
      <table style={tableStyle}>
        <colgroup>
          <col style={{ width: "16%" }} />
          <col style={{ width: "18%" }} />
          <col style={{ width: "14%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "11%" }} />
        </colgroup>
        <thead>
          <tr>
            <td colSpan={8} style={{ border, padding: 0 }}>
              <HeaderPage />
            </td>
          </tr>
          {sectionHeader("I. PERSONAL INFORMATION")}
        </thead>
        <tbody>
          <tr>
            <td style={labelCell}>1. SURNAME</td>
            <td colSpan={7} style={cell}>
              {employee?.surname || ""}
            </td>
          </tr>
          <tr>
            <td style={labelCell}>2. FIRST NAME</td>
            <td colSpan={4} style={cell}>
              {employee?.Firstname || ""}
            </td>
            <td colSpan={3} style={labelCell}>
              <div style={{ fontSize: "6px" }}>NAME EXTENSION (JR., SR)</div>
              <div style={{ fontSize: "8px" }}>
                {employee?.Nameextension || "\u00A0"}
              </div>
            </td>
          </tr>
          <tr>
            <td style={labelCell}>MIDDLE NAME</td>
            <td colSpan={7} style={cell}>
              {employee?.Middlename || ""}
            </td>
          </tr>
          <tr>
            <td style={labelCell}>
              3. DATE OF BIRTH
              <br />
              (dd/mm/yyyy)
            </td>
            <td style={cell}>{formatPdsDate(employee?.birthDate)}</td>
            <td colSpan={2} style={labelCell}>
              16. CITIZENSHIP
            </td>
            <td colSpan={4} style={cell}>
              <label style={{ marginRight: "6px" }}>
                <Check
                  checked={matchesCitizenship(citizenship, ["Filipino"])}
                />{" "}
                Filipino
              </label>
              <label>
                <Check
                  checked={matchesCitizenship(citizenship, [
                    "Dual Citizenship",
                    "Dual Citizen - By Birth",
                    "Dual Citizen - By Naturalization",
                    "Dual Citizenship - By Birth",
                    "Dual Citizenship - By Naturalization",
                  ])}
                />{" "}
                Dual Citizenship
              </label>
              <div>
                <label style={{ marginRight: "6px" }}>
                  <Check
                    checked={matchesCitizenship(citizenship, [
                      "Dual Citizen - By Birth",
                      "Dual Citizenship - By Birth",
                    ])}
                  />{" "}
                  by birth
                </label>
                <label>
                  <Check
                    checked={matchesCitizenship(citizenship, [
                      "Dual Citizen - By Naturalization",
                      "Dual Citizenship - By Naturalization",
                    ])}
                  />{" "}
                  by naturalization
                </label>
              </div>
            </td>
          </tr>
          <tr>
            <td style={labelCell}>4. PLACE OF BIRTH</td>
            <td style={cell}>{employee?.birthPlace || ""}</td>
            <td colSpan={2} style={labelCell}>
              If holder of dual citizenship,
              <br />
              please indicate the details.
            </td>
            <td colSpan={4} style={cell}>
              Pls. indicate country: {employee?.dual || ""}
            </td>
          </tr>
          <tr>
            <td style={labelCell}>5. SEX AT BIRTH</td>
            <td style={cell}>
              <label style={{ marginRight: "8px" }}>
                <Check
                  checked={String(employee?.sex || "").toUpperCase() === "MALE"}
                />{" "}
                Male
              </label>
              <label>
                <Check
                  checked={
                    String(employee?.sex || "").toUpperCase() === "FEMALE"
                  }
                />{" "}
                Female
              </label>
            </td>
            <td rowSpan={3} style={labelCell}>
              17. RESIDENTIAL ADDRESS
            </td>
            <td colSpan={5} style={cell}>
              {addressPair(
                "House/Block/Lot No.",
                employee?.residentialHouseNo,
                "Street",
                employee?.residentialStreet,
              )}
            </td>
          </tr>
          <tr>
            <td style={labelCell}>6. CIVIL STATUS</td>
            <td style={cell}>
              {[
                ["Single", ["Single"]],
                ["Married", ["Married"]],
                ["Widow/er", ["Widow/er", "Widowed", "Widow"]],
                ["Separated", ["Separated"]],
                // ["Solo Parent", ["Solo Parent"]],
                ["Others/s", ["Others", "Other/s", "Other"]],
              ].map(([label, keys]) => (
                <label
                  key={label}
                  style={{
                    display: "inline-block",
                    width: "48%",
                    fontSize: "7px",
                  }}
                >
                  <Check checked={matchesCivil(civil, keys)} /> {label}
                </label>
              ))}
            </td>
            <td colSpan={5} style={cell}>
              {addressPair(
                "Subdivision/Village",
                employee?.residentialSubdivision,
                "Barangay",
                employee?.recidentialBrangay,
              )}
            </td>
          </tr>
          <tr>
            <td style={labelCell}>7. HEIGHT (m)</td>
            <td style={cell}>{employee?.height || ""}</td>
            <td colSpan={5} style={cell}>
              {addressPair(
                "City/Municipality",
                employee?.recidentialCity,
                "Province",
                employee?.recidentialProvince,
              )}
            </td>
          </tr>
          <tr>
            <td style={labelCell}>8. WEIGHT (kg)</td>
            <td style={cell}>{employee?.weight || ""}</td>
            <td style={labelCell}>ZIP CODE</td>
            <td colSpan={5} style={cell}>
              {employee?.residentialZipCode || ""}
            </td>
          </tr>
          <tr>
            <td style={labelCell}>9. BLOOD TYPE</td>
            <td style={cell}>{employee?.blood || ""}</td>
            <td rowSpan={3} style={labelCell}>
              18. PERMANENT ADDRESS
            </td>
            <td colSpan={5} style={cell}>
              {addressPair(
                "House/Block/Lot No.",
                employee?.permanentHouseNo,
                "Street",
                employee?.permanentStreet,
              )}
            </td>
          </tr>
          <tr>
            <td style={labelCell}>10. UMID ID NO.</td>
            {/* <td style={cell}>{employee?.gsis || ""}</td> */}
            <td style={cell}>{employee?.umidId || ""}</td>
            <td colSpan={5} style={cell}>
              {addressPair(
                "Subdivision/Village",
                employee?.permanentSubdivision,
                "Barangay",
                employee?.permanentBrangay,
              )}
            </td>
          </tr>
          <tr>
            <td style={labelCell}>11. PAG-IBIG ID NO.</td>
            <td style={cell}>{employee?.pagIbig || ""}</td>
            <td colSpan={5} style={cell}>
              {addressPair(
                "City/Municipality",
                employee?.permanentCity,
                "Province",
                employee?.permanentProvince,
              )}
            </td>
          </tr>
          <tr>
            <td style={labelCell}>12. PHILHEALTH NO.</td>
            <td style={cell}>{employee?.philhealth || ""}</td>
            <td style={labelCell}>ZIP CODE</td>
            <td colSpan={5} style={cell}>
              {employee?.permanentZipCode || ""}
            </td>
          </tr>
          <tr>
            <td style={labelCell}>13. PhilSys Card Number (PCN)</td>
            <td style={cell}>{"\u00A0"}</td>
            <td style={labelCell}>19. TELEPHONE NO.</td>
            <td colSpan={5} style={cell}>
              {employee?.telephone || ""}
            </td>
          </tr>
          <tr>
            <td style={labelCell}>14. TIN NO.</td>
            <td style={cell}>{employee?.tin || ""}</td>
            <td style={labelCell}>20. MOBILE NO.</td>
            <td colSpan={5} style={cell}>
              {employee?.mobile || ""}
            </td>
          </tr>
          <tr>
            <td style={labelCell}>15. AGENCY EMPLOYEE NO.</td>
            <td style={cell}>{employee?.agency || ""}</td>
            <td style={labelCell}>21. E-MAIL ADDRESS (if any)</td>
            <td colSpan={5} style={cell}>
              {employee?.email || ""}
            </td>
          </tr>
        </tbody>
      </table>
    );
  };

  const FamilyBackground = () => {
    const children = object?.familyBackground || [];
    const childRows = Math.max(children.length, 12);
    return (
      <div style={{ display: "flex", alignItems: "stretch" }}>
        <table style={{ ...tableStyle, width: "50%" }}>
          <colgroup>
            <col style={{ width: "34%" }} />
            <col style={{ width: "36%" }} />
            <col style={{ width: "30%" }} />
          </colgroup>
          <thead>{sectionHeader("II. FAMILY BACKGROUND", 3)}</thead>
          <tbody>
            <tr>
              <td style={labelCell}>22. SPOUSE&apos;S SURNAME</td>
              <td colSpan={2} style={cell}>
                {employee?.spousesurName || ""}
              </td>
            </tr>
            <tr>
              <td style={labelCell}>FIRST NAME</td>
              <td style={cell}>{employee?.spousefirstName || ""}</td>
              <td style={labelCell}>
                <div style={{ fontSize: "6px" }}>NAME EXTENSION (JR., SR)</div>
                <div>{employee?.spouseextension || "\u00A0"}</div>
              </td>
            </tr>
            <tr>
              <td style={labelCell}>MIDDLE NAME</td>
              <td colSpan={2} style={cell}>
                {employee?.spousemiddleName || ""}
              </td>
            </tr>
            <tr>
              <td style={labelCell}>OCCUPATION</td>
              <td colSpan={2} style={cell}>
                {employee?.spouseoccupation || ""}
              </td>
            </tr>
            <tr>
              <td style={labelCell}>EMPLOYER/BUSINESS NAME</td>
              <td colSpan={2} style={cell}>
                {employee?.spouseemployer || ""}
              </td>
            </tr>
            <tr>
              <td style={labelCell}>BUSINESS ADDRESS</td>
              <td colSpan={2} style={cell}>
                {employee?.spouseaddress || ""}
              </td>
            </tr>
            <tr>
              <td style={labelCell}>TELEPHONE NO.</td>
              <td colSpan={2} style={cell}>
                {employee?.spousetelephone || ""}
              </td>
            </tr>
            <tr>
              <td style={labelCell}>24. FATHER&apos;S SURNAME</td>
              <td colSpan={2} style={cell}>
                {employee?.fathersurName || ""}
              </td>
            </tr>
            <tr>
              <td style={labelCell}>FIRST NAME</td>
              <td style={cell}>{employee?.fatherName || ""}</td>
              <td style={labelCell}>
                <div style={{ fontSize: "6px" }}>NAME EXTENSION (JR., SR)</div>
                <div>{employee?.fatherExtension || "\u00A0"}</div>
              </td>
            </tr>
            <tr>
              <td style={labelCell}>MIDDLE NAME</td>
              <td colSpan={2} style={cell}>
                {employee?.fathermiddleName || ""}
              </td>
            </tr>
            <tr>
              <td colSpan={3} style={labelCell}>
                25. MOTHER&apos;S MAIDEN NAME
              </td>
            </tr>
            <tr>
              <td style={labelCell}>SURNAME</td>
              <td colSpan={2} style={cell}>
                {employee?.mothermaidenName || ""}
              </td>
            </tr>
            <tr>
              <td style={labelCell}>FIRST NAME</td>
              <td colSpan={2} style={cell}>
                {employee?.motherName || ""}
              </td>
            </tr>
            <tr>
              <td style={labelCell}>MIDDLE NAME</td>
              <td colSpan={2} style={cell}>
                {employee?.mothermiddleName || ""}
              </td>
            </tr>
          </tbody>
        </table>
        <table style={{ ...tableStyle, width: "50%", borderLeft: "none" }}>
          <colgroup>
            <col style={{ width: "65%" }} />
            <col style={{ width: "35%" }} />
          </colgroup>
          <thead>
            <tr>
              <th style={headerBg}>{"\u00A0"}</th>
              <th style={headerBg}>{"\u00A0"}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={centerLabel}>
                23. NAME of CHILDREN (Write full name and list all)
              </td>
              <td style={centerLabel}>DATE OF BIRTH (dd/mm/yyyy)</td>
            </tr>
            {Array.from({ length: childRows }).map((_, i) => {
              const item = children[i] || {};
              return (
                <tr key={`child-${i}`}>
                  <td style={{ ...cell, minHeight: "12px" }}>
                    {item.name || "\u00A0"}
                  </td>
                  <td style={cell}>{formatPdsDate(item.birth) || "\u00A0"}</td>
                </tr>
              );
            })}
            {continueNote("(Continue on separate sheet if necessary)", 2)}
          </tbody>
        </table>
      </div>
    );
  };

  const EducationalBackground = () => {
    const rows = object?.educationalBackground || [];
    const used = new Set();
    const officialRows = EDUCATION_LEVELS.map((level) => {
      const match = findEducationRow(rows, level.keys);
      if (match) used.add(match);
      return { level: level.label, item: match || {} };
    });
    const extras = rows.filter((row) => !used.has(row));
    const displayRows = [...officialRows, ...extras.map((item) => ({
      level: item?.educationLevel || "",
      item,
    }))];

    return (
      <table style={tableStyle}>
        <colgroup>
          <col style={{ width: "16%" }} />
          <col style={{ width: "20%" }} />
          <col style={{ width: "20%" }} />
          <col style={{ width: "7%" }} />
          <col style={{ width: "7%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "12%" }} />
        </colgroup>
        <thead>
          {sectionHeader("III. EDUCATIONAL BACKGROUND")}
          <tr>
            <th rowSpan={2} style={centerLabel}>
              26.
              <br />
              LEVEL
            </th>
            <th rowSpan={2} style={centerLabel}>
              NAME OF SCHOOL
              <br />
              (Write in full)
            </th>
            <th rowSpan={2} style={centerLabel}>
              BASIC EDUCATION/DEGREE/COURSE
              <br />
              (Write in full)
            </th>
            <th colSpan={2} style={centerLabel}>
              PERIOD OF ATTENDANCE
              <br />
              (yyyy)
            </th>
            <th rowSpan={2} style={centerLabel}>
              HIGHEST LEVEL/ UNITS EARNED
              <br />
              (if not graduated)
            </th>
            <th rowSpan={2} style={centerLabel}>
              YEAR GRADUATED
            </th>
            <th rowSpan={2} style={centerLabel}>
              SCHOLARSHIP/ ACADEMIC HONORS RECEIVED
            </th>
          </tr>
          <tr>
            <th style={centerLabel}>From</th>
            <th style={centerLabel}>To</th>
          </tr>
        </thead>
        <tbody>
          {displayRows.map((row, i) => {
            const item = row.item || {};
            return (
              <tr key={`educ-${i}`}>
                <td style={cell}>{row.level || "\u00A0"}</td>
                <td style={cell}>{item.educationSchool || "\u00A0"}</td>
                <td style={cell}>{item.educationDegree || "\u00A0"}</td>
                <td style={cell}>
                  {formatPdsYear(item.educationFrom) || "\u00A0"}
                </td>
                <td style={cell}>
                  {formatPdsYear(item.educationTo) || "\u00A0"}
                </td>
                <td style={cell}>{item.educationHighest || "\u00A0"}</td>
                <td style={cell}>{item.educationYear || "\u00A0"}</td>
                <td style={cell}>{item.educationHonors || "\u00A0"}</td>
              </tr>
            );
          })}
          {continueNote("(Continue on separate sheet if necessary)", 8)}
          {signatureRow(8)}
        </tbody>
      </table>
    );
  };

  const CivilServiceEligibility = () => {
    const rows = object?.civilServiceBackground || [];
    const count = Math.max(rows.length, 7);
    return (
      <table style={tableStyle}>
        <colgroup>
          <col style={{ width: "30%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "28%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "10%" }} />
        </colgroup>
        <thead>
          {sectionHeader("IV. CIVIL SERVICE ELIGIBILITY", 6)}
          <tr>
            <th rowSpan={2} style={centerLabel}>
              27.
              <br />
              CAREER SERVICE/ RA 1080 (BOARD/ BAR) UNDER SPECIAL LAWS/ CES/ CSEE
              BARANGAY ELIGIBILITY / DRIVER&apos;S LICENSE
            </th>
            <th rowSpan={2} style={centerLabel}>
              RATING
              <br />
              (If Applicable)
            </th>
            <th rowSpan={2} style={centerLabel}>
              DATE OF EXAMINATION / CONFERMENT
            </th>
            <th rowSpan={2} style={centerLabel}>
              PLACE OF EXAMINATION / CONFERMENT
            </th>
            <th colSpan={2} style={centerLabel}>
              LICENSE (if applicable)
            </th>
          </tr>
          <tr>
            <th style={centerLabel}>NUMBER</th>
            <th style={centerLabel}>Date of Validity</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: count }).map((_, i) => {
            const item = rows[i] || {};
            return (
              <tr key={`cse-${i}`}>
                <td style={cell}>{item.civilCareer || "\u00A0"}</td>
                <td style={cell}>{item.civilRating || "\u00A0"}</td>
                <td style={cell}>
                  {formatPdsDate(item.civilExamination) || "\u00A0"}
                </td>
                <td style={cell}>{item.civilPlace || "\u00A0"}</td>
                <td style={cell}>{item.civilLicense || "\u00A0"}</td>
                <td style={cell}>
                  {formatPdsDate(item.civilValidity) || "\u00A0"}
                </td>
              </tr>
            );
          })}
          {continueNote("(Continue on separate sheet if necessary)", 6)}
        </tbody>
      </table>
    );
  };

  const WorkExperience = () => {
    const rows = object?.workExperienceBackground || [];
    const count = Math.max(rows.length, 24);
    return (
      <table style={tableStyle}>
        <colgroup>
          <col style={{ width: "8%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "18%" }} />
          <col style={{ width: "20%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "14%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "10%" }} />
        </colgroup>
        <thead>
          {sectionHeader("V. WORK EXPERIENCE", 8)}
          <tr>
            <th colSpan={8} style={{ ...labelCell, fontStyle: "italic" }}>
              (Include private employment. Start from your recent work)
              Description of duties should be indicated in the attached Work
              Experience sheet.
            </th>
          </tr>
          <tr>
            <th colSpan={2} style={centerLabel}>
              28.
              <br />
              INCLUSIVE DATES (dd/mm/yyyy)
            </th>
            <th rowSpan={2} style={centerLabel}>
              POSITION TITLE
              <br />
              (Write in full/Do not abbreviate)
            </th>
            <th rowSpan={2} style={centerLabel}>
              DEPARTMENT / AGENCY / OFFICE / COMPANY (Write in full/Do not
              abbreviate)
            </th>
            <th rowSpan={2} style={centerLabel}>
              MONTHLY SALARY
            </th>
            <th rowSpan={2} style={centerLabel}>
              SALARY/ JOB/ PAY GRADE (if applicable)&amp; STEP INCREMENT (Format
              &quot;00-0&quot;)
            </th>
            <th rowSpan={2} style={centerLabel}>
              STATUS OF APPOINTMENT
            </th>
            <th rowSpan={2} style={centerLabel}>
              GOV&apos;T SERVICE (Y/ N)
            </th>
          </tr>
          <tr>
            <th style={centerLabel}>From</th>
            <th style={centerLabel}>To</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: count }).map((_, i) => {
            const item = rows[i] || {};
            return (
              <tr key={`work-${i}`}>
                <td style={cell}>{formatPdsDate(item.from) || "\u00A0"}</td>
                <td style={cell}>{formatPdsDate(item.to) || "\u00A0"}</td>
                <td style={cell}>{item.position || "\u00A0"}</td>
                <td style={cell}>{item.department || "\u00A0"}</td>
                <td style={cell}>{item.salary || "\u00A0"}</td>
                <td style={cell}>{item.grade || "\u00A0"}</td>
                <td style={cell}>{item.appointment || "\u00A0"}</td>
                <td style={cell}>{item.service || "\u00A0"}</td>
              </tr>
            );
          })}
          {continueNote("(Continue on separate sheet if necessary)", 8)}
          {signatureRow(8)}
        </tbody>
      </table>
    );
  };

  const LearningAndDevelopment = () => {
    const rows = object?.learningDevBackground || [];
    const count = Math.max(rows.length, 17);
    return (
      <table style={tableStyle}>
        <colgroup>
          <col style={{ width: "32%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "14%" }} />
          <col style={{ width: "26%" }} />
        </colgroup>
        <thead>
          {sectionHeader(
            "VI. LEARNING AND DEVELOPMENT (L&D) INTERVENTIONS/TRAINING PROGRAMS ATTENDED",
            6,
          )}
          <tr>
            <th rowSpan={2} style={centerLabel}>
              29.
              <br />
              TITLE OF LEARNING AND DEVELOPMENT INTERVENTIONS/TRAINING PROGRAMS
              (Write in full)
            </th>
            <th colSpan={2} style={centerLabel}>
              INCLUSIVE DATES OF ATTENDANCE
              <br />
              (dd/mm/yyyy)
            </th>
            <th rowSpan={2} style={centerLabel}>
              NUMBER OF HOURS
            </th>
            <th rowSpan={2} style={centerLabel}>
              Type of L&amp;D (Managerial/ Supervisory/ Technical/etc)
            </th>
            <th rowSpan={2} style={centerLabel}>
              CONDUCTED/ SPONSORED BY (Write in full)
            </th>
          </tr>
          <tr>
            <th style={centerLabel}>From</th>
            <th style={centerLabel}>To</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: count }).map((_, i) => {
            const item = rows[i] || {};
            return (
              <tr key={`ld-${i}`}>
                <td style={cell}>{item.learningTitle || "\u00A0"}</td>
                <td style={cell}>
                  {formatPdsDate(item.learningFrom) || "\u00A0"}
                </td>
                <td style={cell}>
                  {formatPdsDate(item.learningTo) || "\u00A0"}
                </td>
                <td style={cell}>{item.learningHours || "\u00A0"}</td>
                <td style={cell}>{item.learningType || "\u00A0"}</td>
                <td style={cell}>{item.learningConducted || "\u00A0"}</td>
              </tr>
            );
          })}
          {continueNote("(Continue on separate sheet if necessary)", 6)}
        </tbody>
      </table>
    );
  };

  const VoluntaryWork = () => {
    const rows = object?.voluntaryWorkBackground || [];
    const count = Math.max(rows.length, 9);
    return (
      <table style={tableStyle}>
        <colgroup>
          <col style={{ width: "36%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "32%" }} />
        </colgroup>
        <thead>
          {sectionHeader(
            "VII. VOLUNTARY WORK OR INVOLVEMENT IN CIVIC / NON-GOVERNMENT / PEOPLE / VOLUNTARY ORGANIZATION/S",
            5,
          )}
          <tr>
            <th rowSpan={2} style={centerLabel}>
              30.
              <br />
              NAME &amp; ADDRESS OF ORGANIZATION (Write in full)
            </th>
            <th colSpan={2} style={centerLabel}>
              INCLUSIVE DATES
              <br />
              (dd/mm/yyyy)
            </th>
            <th rowSpan={2} style={centerLabel}>
              NUMBER OF HOURS
            </th>
            <th rowSpan={2} style={centerLabel}>
              POSITION / NATURE OF WORK
            </th>
          </tr>
          <tr>
            <th style={centerLabel}>From</th>
            <th style={centerLabel}>To</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: count }).map((_, i) => {
            const item = rows[i] || {};
            return (
              <tr key={`vol-${i}`}>
                <td style={cell}>
                  {item.voluntaryOrganization || item.name || "\u00A0"}
                </td>
                <td style={cell}>
                  {formatPdsDate(item.voluntaryFrom) || "\u00A0"}
                </td>
                <td style={cell}>
                  {formatPdsDate(item.voluntaryTo) || "\u00A0"}
                </td>
                <td style={cell}>{item.voluntaryHours || "\u00A0"}</td>
                <td style={cell}>{item.voluntaryPosition || "\u00A0"}</td>
              </tr>
            );
          })}
          {continueNote("(Continue on separate sheet if necessary)", 5)}
        </tbody>
      </table>
    );
  };

  const OtherInformation = () => {
    const rows = object?.otherInformationBackground || [];
    const count = Math.max(rows.length, 7);
    return (
      <table style={tableStyle}>
        <colgroup>
          <col style={{ width: "28%" }} />
          <col style={{ width: "40%" }} />
          <col style={{ width: "32%" }} />
        </colgroup>
        <thead>
          {sectionHeader("VIII. OTHER INFORMATION", 3)}
          <tr>
            <th style={centerLabel}>31. SPECIAL SKILLS and HOBBIES</th>
            <th style={centerLabel}>
              32. NON-ACADEMIC DISTINCTIONS / RECOGNITION (Write in full)
            </th>
            <th style={centerLabel}>
              33. MEMBERSHIP IN ASSOCIATION/ORGANIZATION (Write in full)
            </th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: count }).map((_, i) => {
            const item = rows[i] || {};
            return (
              <tr key={`oth-${i}`}>
                <td style={cell}>
                  {item.specialSkills || item.skill || "\u00A0"}
                </td>
                <td style={cell}>
                  {item.recognition || item.academic || "\u00A0"}
                </td>
                <td style={cell}>{item.membership || "\u00A0"}</td>
              </tr>
            );
          })}
          {continueNote("(Continue on separate sheet if necessary)", 3)}
          {signatureRow(3)}
        </tbody>
      </table>
    );
  };

  const yesNoDetails = (checked, details, detailsLabel = "If YES, give details:") => (
    <div style={{ padding: "3px 4px" }}>
      <div>
        <label style={{ marginRight: "10px" }}>
          <Check checked={checked} /> YES
        </label>
        <label>
          <Check checked={!checked} /> NO
        </label>
      </div>
      <div style={{ marginTop: "2px" }}>{detailsLabel}</div>
      <div style={{ minHeight: "12px", borderBottom: "1px solid #000" }}>
        {details || "\u00A0"}
      </div>
    </div>
  );

  const LastPage = () => {
    const q = employee;
    const questionRow = (number, label, value, extraStyle) => (
      <tr>
        <td style={{ ...labelCell, ...extraStyle }}>
          <div style={{ display: "flex" }}>
            <span style={{ marginRight: "4px", fontWeight: "bold" }}>
              {number}
            </span>
            <span>{label}</span>
          </div>
        </td>
        <td style={{ ...cell, padding: 0 }}>{value}</td>
      </tr>
    );

    return (
      <table style={tableStyle}>
        <colgroup>
          <col style={{ width: "68%" }} />
          <col style={{ width: "32%" }} />
        </colgroup>
        <tbody>
          {questionRow(
            "34.",
            <span>
              Are you related by consanguinity or affinity to the appointing or
              recommending authority, or to the chief of bureau or office or to
              the person who has immediate supervision over you in the Office,
              Bureau or Department where you will be apppointed,
              <br />
              a. within the third degree?
              <br />
              b. within the fourth degree (for Local Government Unit - Career
              Employees)?
            </span>,
            <div>
              {yesNoDetails(isYes(q?.questionOne), q?.questionOne)}
              {yesNoDetails(isYes(q?.questionTwo), q?.questionTwo)}
            </div>,
          )}
          {questionRow(
            "35.",
            <span>
              a. Have you ever been found guilty of any administrative offense?
              <br />
              <br />
              b. Have you been criminally charged before any court?
            </span>,
            <div>
              {yesNoDetails(isYes(q?.questionThree), q?.questionThree)}
              {yesNoDetails(isYes(q?.questionFour), q?.questionFour)}
              <div style={{ padding: "2px 4px" }}>
                Date Filed:{" "}
                <span style={{ borderBottom: "1px solid #000" }}>
                  {formatPdsDate(q?.questionFourDate) || "\u00A0\u00A0\u00A0"}
                </span>
                <br />
                Status of Case/s:{" "}
                <span style={{ borderBottom: "1px solid #000" }}>
                  {q?.questionFourStat || "\u00A0\u00A0\u00A0"}
                </span>
              </div>
            </div>,
          )}
          {questionRow(
            "36.",
            "Have you ever been convicted of any crime or violation of any law, decree, ordinance or regulation by any court or tribunal?",
            yesNoDetails(isYes(q?.questionFive), q?.questionFive),
          )}
          {questionRow(
            "37.",
            "Have you ever been separated from the service in any of the following modes: resignation, retirement, dropped from the rolls, dismissal, termination, end of term, finished contract or phased out (abolition) in the public or private sector?",
            yesNoDetails(isYes(q?.questionTwelve), q?.questionTwelve),
          )}
          {questionRow(
            "38.",
            <span>
              a. Have you ever been a candidate in a national or local election
              held within the last year (except Barangay election)?
              <br />
              <br />
              b. Have you resigned from the government service during the three
              (3)-month period before the last election to promote/actively
              campaign for a national or local candidate?
            </span>,
            <div>
              {yesNoDetails(isYes(q?.questionSix), q?.questionSix)}
              {yesNoDetails(isYes(q?.questionSeven), q?.questionSeven)}
            </div>,
          )}
          {questionRow(
            "39.",
            "Have you acquired the status of an immigrant or permanent resident of another country?",
            yesNoDetails(
              isYes(q?.questionEight),
              q?.questionEight,
              "If YES, give details (country):",
            ),
          )}
          {questionRow(
            "40.",
            <span>
              Pursuant to: (a) Indigenous People&apos;s Act (RA 8371); (b) Magna
              Carta for Disabled Persons (RA 7277, as amended); and (c) Expanded
              Solo Parents Welfare Act (RA 11861), please answer the following
              items:
              <br />
              a. Are you a member of any indigenous group?
              <br />
              b. Are you a person with disability?
              <br />
              c. Are you a solo parent?
            </span>,
            <div>
              {yesNoDetails(
                isYes(q?.questionNine),
                q?.questionNine,
                "If YES, please specify:",
              )}
              {yesNoDetails(
                isYes(q?.questionTen),
                q?.questionTen,
                "If YES, please specify ID No:",
              )}
              {yesNoDetails(
                isYes(q?.questionEleven),
                q?.questionEleven,
                "If YES, please specify ID No:",
              )}
            </div>,
          )}
        </tbody>
      </table>
    );
  };

  const ReferencesAndDeclaration = () => {
    const refs = object?.referenceBackground || [];
    const count = Math.max(refs.length, 3);
    return (
      <div style={{ display: "flex" }}>
        <table style={{ ...tableStyle, width: "75%" }}>
          <colgroup>
            <col style={{ width: "34%" }} />
            <col style={{ width: "36%" }} />
            <col style={{ width: "30%" }} />
          </colgroup>
          <thead>
            <tr>
              <th colSpan={3} style={{ ...headerBg, fontStyle: "normal" }}>
                41. REFERENCES{" "}
                <span style={{ color: "#ffd2d2" }}>
                  (Person not related by consanguinity or affinity to applicant
                  /appointee)
                </span>
              </th>
            </tr>
            <tr>
              <th style={centerLabel}>NAME</th>
              <th style={centerLabel}>OFFICE / RESIDENTIAL ADDRESS</th>
              <th style={centerLabel}>CONTACT NO. AND/OR EMAIL</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: count }).map((_, i) => {
              const item = refs[i] || {};
              return (
                <tr key={`ref-${i}`}>
                  <td style={cell}>{item.referenceName || "\u00A0"}</td>
                  <td style={cell}>{item.referenceAddress || "\u00A0"}</td>
                  <td style={cell}>{item.referenceTel || "\u00A0"}</td>
                </tr>
              );
            })}
            <tr>
              <td colSpan={3} style={{ ...cell, padding: "6px" }}>
                <b>42. </b>
                I declare under oath that I have personally accomplished this
                Personal Data Sheet which is a true, correct, and complete
                statement pursuant to the provisions of pertinent laws, rules,
                and regulations of the Republic of the Philippines. I authorize
                the agency head/authorized representative to verify/validate the
                contents stated herein. I agree that any misrepresentation made
                in this document and its attachments shall cause the filing of
                administrative/criminal case/s against me.
              </td>
            </tr>
            <tr>
              <td style={{ ...cell, padding: "6px" }}>
                <div style={{ ...labelBg, border, padding: "3px", fontSize: "6.5px" }}>
                  Government Issued ID (i.e.Passport, GSIS, SSS, PRC,
                  Driver&apos;s License, etc.)
                  <br />
                  PLEASE INDICATE ID Number and Date of Issuance
                </div>
                <div style={{ ...cell, borderTop: "none" }}>
                  Government Issued ID: {employee?.government11 || ""}
                </div>
                <div style={cell}>
                  ID/License/Passport No.: {employee?.passport11 || ""}
                </div>
                <div style={cell}>
                  Date/Place of Issuance: {employee?.issurance11 || ""}
                </div>
              </td>
              <td colSpan={2} style={{ ...cell, padding: "6px" }}>
                <div
                  style={{
                    border,
                    minHeight: "42px",
                    textAlign: "center",
                    fontSize: "6.5px",
                    paddingTop: "4px",
                  }}
                >
                  (e-signature/digital certificate)
                </div>
                <div style={{ ...centerLabel, borderTop: "none" }}>
                  Signature (Sign inside the box)
                </div>
                <div style={{ ...cell, textAlign: "center", minHeight: "16px" }}>
                  {formatPdsDate(new Date())}
                </div>
                <div style={centerLabel}>Date Accomplished</div>
              </td>
            </tr>
          </tbody>
        </table>
        <div
          style={{
            width: "25%",
            border: border,
            borderLeft: "none",
            boxSizing: "border-box",
            textAlign: "center",
            padding: "6px 4px",
            fontSize: "7px",
          }}
        >
          <div
            style={{
              width: "3.5cm",
              height: "4.5cm",
              border,
              margin: "0 auto",
              padding: "4px",
              fontSize: "6.5px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ID picture taken within the last 6 months
            <br />
            4.5 cm. X 3.5 cm (passport size)
            <br />
            <br />
            With full and handwritten name tag and signature over printed name
            <br />
            Computer generated or photocopied picture is not acceptable
          </div>
          <div style={{ margin: "4px 0", fontWeight: "bold" }}>PHOTO</div>
          <div
            style={{
              width: "80%",
              height: "3.4cm",
              border,
              margin: "0 auto",
              position: "relative",
            }}
          >
            <div
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                borderTop: border,
                ...labelBg,
                fontSize: "7px",
                padding: "2px 0",
              }}
            >
              Right Thumbmark
            </div>
          </div>
        </div>
      </div>
    );
  };

  const OathBlock = () => (
    <table style={tableStyle}>
      <tbody>
        <tr>
          <td style={{ ...cell, textAlign: "center", padding: "8px" }}>
            SUBSCRIBED AND SWORN to before me this ________________, affiant
            exhibiting his/her validly issued government ID as indicated above.
          </td>
        </tr>
        <tr>
          <td style={{ ...cell, textAlign: "center", padding: "8px" }}>
            <div
              style={{
                width: "45%",
                margin: "0 auto",
                border,
              }}
            >
              <div style={{ minHeight: "48px", fontSize: "6.5px", paddingTop: "4px" }}>
                (e-signature/digital certificate except for notary public)
              </div>
              <div style={centerLabel}>Person Administering Oath</div>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  );

  const Page = ({ children }) => (
    <div className="pds-page">{children}</div>
  );

  const Footer = ({ page, total }) => (
    <div
      style={{
        fontSize: "7.5px",
        textAlign: "right",
        paddingTop: "3px",
        fontWeight: "bold",
      }}
    >
      CS FORM 212 (Revised 2026), Page {page} of {total}
    </div>
  );

  return (
    <div className="pds-print-root">
      <Page>
        <PersonalInformationTable />
        <FamilyBackground />
        <EducationalBackground />
        <Footer page={1} total={4} />
      </Page>
      <Page>
        <CivilServiceEligibility />
        <WorkExperience />
        <Footer page={2} total={4} />
      </Page>
      <Page>
        <LearningAndDevelopment />
        <VoluntaryWork />
        <OtherInformation />
        <Footer page={3} total={4} />
      </Page>
      <Page>
        <LastPage />
        <ReferencesAndDeclaration />
        <OathBlock />
        <Footer page={4} total={4} />
      </Page>
    </div>
  );
}
